import { delayForMs } from './utils';

export type RetryOptions<T = any> = {
  retries: number; // Number of retries
  initialDelay: number; // Initial delay between retries in milliseconds
  delayFunction?: (attemptNumber: number, initialDelay: number) => number; // Function to calculate delay (optional)
  shouldRetry?: (error: unknown) => Promise<boolean>; // Predicate function to determine whether to retry
  onRetry?: (error: unknown, attemptNumber: number, delay: number) => void; // Optional callback function to execute on each retry
  signal?: AbortSignal; // AbortSignal to cancel the operation
};

export async function retry<T>(fn: () => Promise<T>, options: RetryOptions<T>, attemptNumber: number = 1): Promise<T> {
  if (options.signal?.aborted) {
    throw new Error('Operation aborted');
  }

  try {
    return await fn();
  } catch (error) {
    const shouldRetry = options.shouldRetry ? await options.shouldRetry(error) : true;

    if (attemptNumber > options.retries || !shouldRetry || options.signal?.aborted) {
      // Check if attemptNumber exceeds retries, or shouldRetry returns false, or signal is aborted
      throw error;
    }

    const delay = options.delayFunction
      ? options.delayFunction(attemptNumber, options.initialDelay) // Pass attemptNumber to delayFunction
      : options.initialDelay * Math.pow(2, attemptNumber - 1); // always use an exponential backoff if delayFn not explicitly set

    if (options.onRetry) {
      options.onRetry(error, attemptNumber, delay);
    }

    await delayForMs(delay, options.signal);

    return retry(fn, options, attemptNumber + 1); // Increment attemptNumber for the next retry
  }
}
